import { z } from "zod";

import {
  type BuiltinThemeName,
  builtinThemeId,
  type ThemeId,
  ThemeMode,
} from "./theme.ts";

/**
 * A screen owns both its theme and its mode. The file's copy of the same two
 * fields is only the default for the next generated screen, so changing it
 * never repaints work already on the canvas. Resolution falls through to the
 * file only for a screen that has never stated anything — pasted in, parsed
 * from JSX, or predating the fields.
 *
 * Source and mode are stored separately so a screen can be dark without that
 * reading as a theme override.
 */

/**
 * A floating source resolves through `themes.published_version` on every read,
 * so publishing is one integer update rather than a fan-out across files.
 * Pinning a number is the opt-out for a screen someone wants frozen; nothing
 * writes one yet.
 */
export const ThemeSource = z.object({
  /** `builtin:blue`, or the uuid of a row in `flowstep.themes`. */
  id: z.string(),
  /** Follow the theme's published version, or hold at this one. */
  version: z.union([z.literal("published"), z.number().int().positive()]),
});
export type ThemeSource = z.infer<typeof ThemeSource>;

/** Resolved, never stored — what {@link resolveThemeRef} produces. */
export const ThemeRef = ThemeSource.extend({ mode: ThemeMode });
export type ThemeRef = z.infer<typeof ThemeRef>;

/**
 * On a screen: what it wears. On the root: what the next generated screen
 * starts from.
 */
export const ThemedElementFields = z.object({
  themeSource: ThemeSource.optional(),
  themeMode: ThemeMode.optional(),
});
export type ThemedElementFields = z.infer<typeof ThemedElementFields>;

export const defaultThemeSource = (): ThemeSource => ({
  id: "builtin:default",
  version: "published",
});

export const DEFAULT_THEME_MODE: ThemeMode = "light";

export const resolveThemeSource = (
  screen: ThemedElementFields | undefined,
  file: ThemedElementFields | undefined,
): ThemeSource =>
  screen?.themeSource ?? file?.themeSource ?? defaultThemeSource();

/**
 * The file's theme as a default for NEW screens. A deleted theme keeps
 * resolving for the screens already wearing it, but nothing new starts in it.
 */
export const liveThemeSource = (
  themeSource: ThemeSource | undefined,
  archivedThemeIds: readonly ThemeId[],
): ThemeSource | undefined =>
  themeSource !== undefined && !archivedThemeIds.includes(themeSource.id)
    ? themeSource
    : undefined;

export const resolveThemeMode = (
  screen: ThemedElementFields | undefined,
  file: ThemedElementFields | undefined,
): ThemeMode => screen?.themeMode ?? file?.themeMode ?? DEFAULT_THEME_MODE;

export const resolveThemeRef = (
  screen: ThemedElementFields | undefined,
  file: ThemedElementFields | undefined,
): ThemeRef => ({
  ...resolveThemeSource(screen, file),
  mode: resolveThemeMode(screen, file),
});

/** Floating: a built-in has no versions to pin to. */
export const builtinThemeSource = (name: BuiltinThemeName): ThemeSource => ({
  id: builtinThemeId(name),
  version: "published",
});

/** A built-in, resolved. Mostly for tests. */
export const builtinThemeRef = (
  name: BuiltinThemeName,
  mode: ThemeMode = DEFAULT_THEME_MODE,
): ThemeRef => ({ ...builtinThemeSource(name), mode });

export const themedFieldsFor = (
  ref: ThemeRef,
): Required<ThemedElementFields> => ({
  themeSource: { id: ref.id, version: ref.version },
  themeMode: ref.mode,
});
